import { useI18next, useTranslation } from 'gatsby-plugin-react-i18next';
import { Box, Flex, Icon, Link as JLink, ListItem, Stack } from 'juhuui';
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import shallow from 'zustand/shallow';

import Image from '../components/image';
import LinkWithIcon from '../components/linkWithIcon';
import SEO from '../components/seo';
import useIntersectionObserver from '../hooks/useIntersectionObserver';
import {
    ContainerOuter, grid12, GridHalfFull, HeadingLg, HeadingMd, HeadingSm, LinkFrame, Bright,
    TextMd
} from '../styles';
import theme from '../theme';
import { Disease, GraphQLData } from '../types';
import { getLocalizationObj } from '../utilities/getLocalizationObj';
import { useStore } from '../zustand';
import Accordion from './accordion';
import ListYBorder from './listYBorder';
import Markdown from './markdown';

export const ServicesMethods = forwardRef(
  ({ methodsTitle, methodCover, methodsSubtitle, methods }, ref) => {
    const { t } = useTranslation();

    const [methodsInView] = useIntersectionObserver(ref, {});

    const [setSubNavigationServices, setServicesInView] = useStore(
      (state) => [state.setSubNavigationServices, state.setServicesInView],
      shallow
    );

    useEffect(() => {
      setSubNavigationServices(0);
      if (methodsInView) {
        setServicesInView({ 0: true });
      } else {
        setServicesInView({ 0: false });
      }
    }, [methodsInView]);

    // set overlay content
    const [setModalLevel1Visible, setModalLevel1Data] = useStore(
      (state) => [state.setModalLevel1Visible, state.setModalLevel1Data],
      shallow
    );
    const changeModal = ({ id, ...rest }, type: string) => {
      setModalLevel1Data({ name: type, id, ...rest });
      setModalLevel1Visible(true);
    };

    return (
      <Bright>
        <ContainerOuter
          ref={ref}
          className={grid12}
          css={{
            bg: 'tom.bgBright',
            margin: 'auto',
            maxW: 'var(--max-width)',
            px: 'var(--px)',
          }}
          // pb={['32']}
        >
          <GridHalfFull
            css={{
              column: ['1/span 12', undefined, '1/span 5'],
              h: [undefined, undefined, `calc(90vh - ${theme.sizes[20]})`],
              pos: ['relative', undefined, 'sticky'],
              top: [undefined, undefined, '20'],
            }}
            fw={{ first: true }}
          >
            <HeadingLg
              as="h2"
              classNames="col-span-12 lg:min-h-[8rem] pb-10 pointer-events-none whitespace-pre-wrap z-1"
              // css={{
              //   column: '1/span 12',
              //   minHeight: [undefined, undefined, '8rem'],
              //   pb: ['10'],
              //   pointerEvents: 'none',
              //   whiteSpace: 'pre-wrap',
              //   zIndex: '1',
              // }}
            >
              {methodsTitle}
            </HeadingLg>

            <Box>
              <Image
                alt={methodCover?.alternativeText}
                css={{
                  rounded: 'sm',
                  h: [undefined, undefined, `calc(90vh - 13rem)`],
                  maxH: ['20rem', undefined, '40rem'],
                }}
                image={methodCover?.file}
                srcsetWidthDesktop="35vw"
              />
            </Box>
          </GridHalfFull>
          <GridHalfFull
            css={{
              pb: ['', undefined, '40vh'],
              pt: [6, undefined, '32'],
              media: {
                '(orientation: portrait) and (min-width: 62rem)': {
                  pb: '20vh',
                },
              },
            }}
          >
            <HeadingSm as="h3" classNames="pb-3 pt-6 lg:pt-0 pr-[33%]">
              {methodsSubtitle}
            </HeadingSm>
            <ListYBorder type="div">
              {methods &&
                methods.map((method, index) => {
                  if(!getLocalizationObj(method))
                    return null;
                  const { title, teaser, id } = getLocalizationObj(method);
                  return (
                    <Accordion
                      key={index}
                      header={(open) => (
                        <LinkFrame
                          css={{
                            d: 'flex',
                            align: 'center',
                            justify: 'space-between',
                            py: ['3'],
                          }}
                          icon="hover"
                          text="hover"
                        >
                          <HeadingMd
                            as="h4"
                            classNames={`link-text ${
                              open ? 'text-textHighlight' : ''
                            } m-0 p-0`}
                            // css={{
                            //   color: open ? 'tom.textHighlight' : '',
                            //   m: '0',
                            //   p: '0',
                            // }}
                          >
                            {title}
                          </HeadingMd>
                          <Icon
                            className="link-icon"
                            css={{
                              mr: ['2'],
                              size: ['8'],
                              flexShrink: '0',
                              ml: '3',
                            }}
                            name={open ? 'close' : 'open'}
                          />
                        </LinkFrame>
                      )}
                      indent={false}
                      openDefault={index === 0}
                      // pt={['2']}
                    >
                      {teaser && (
                        <TextMd
                          as="div"
                          css={{
                            w: ['90%', undefined, '80%'],
                            pt: [6],
                            hyphens: 'auto',
                          }}
                        >
                          <Markdown smallList>{teaser}</Markdown>
                        </TextMd>
                      )}
                      <LinkWithIcon
                        css={{
                          d: 'flex',
                          pb: ['10'],
                          pt: ['8'],
                        }}
                        text={t('more information')}
                        onClick={() =>
                          changeModal(getLocalizationObj(method), 'method')
                        }
                      />
                    </Accordion>
                  );
                })}
            </ListYBorder>
          </GridHalfFull>
        </ContainerOuter>
      </Bright>
    );
  }
);
